// pages/Gallery.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { client } from '../lib/client.js';
import { galleryCampaignsQuery } from '../lib/queries.js';
import Layout from '../components/Layout.jsx';

function Gallery() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);

  // Persist “first autoplay assigned” across renders
  const firstAutoplayAssignedRef = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let cancelled = false;
    client.fetch(galleryCampaignsQuery).then((data) => {
      if (!cancelled) setCampaigns(data || []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleGalleryClick = (slug) => {
    navigate(`/pages/gallery/${slug}`);
  };

  const canonicalUrl = 'https://this-belongs-to.com/pages/galleries';
  const metaTitle = 'Gallery | This Belongs To';
  const metaDescription =
    'Campaign gallery for This Belongs To: underwear and clothing campaigns and lookbooks.';

  return (
    <Layout>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>

      <div className="w-full bg-white flex flex-col items-start justify-start py-2 uppercase italic mt-2 md:mt-8">
        {campaigns.map((item, index) => {
          const positionClasses =
            index % 3 === 0
              ? 'ml-16'
              : index % 3 === 1
              ? 'ml-auto mr-16'
              : 'mx-auto';

          // Only the first campaign with a video gets autoplay (persisted across renders)
          {/* const shouldAutoPlay =
            !firstAutoplayAssignedRef.current && !!item.videoUrl;

          if (shouldAutoPlay) {
            firstAutoplayAssignedRef.current = true;
          } */}

          return (
            <React.Fragment key={item._id || item.slug || index}>
              {/* Optional video row above the campaign (NOT a link / NOT clickable) */}
              {item.videoUrl && (
                <div className="w-11/12 md:w-3/5 mx-auto mb-14">
                  <div className="aspect-[16/9] w-full overflow-hidden rounded-sm">
                    <video
                      src={item.videoUrl}
                      className="w-full h-full object-cover"
                      controls
                      muted
                      playsInline
                      autoPlay
                      loop
                      // Reduce initial load/bandwidth for non-first videos
                      // preload={shouldAutoPlay ? 'metadata' : 'none'}
                      // Ensure controls clicks don’t accidentally trigger anything parent-y
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              )}

              {/* Campaign card (THIS is the link/click target) */}
              <div
                className={`group relative w-4/5 md:w-2/5 mb-8 md:mt-2 cursor-pointer ${positionClasses}`}
                onClick={() => handleGalleryClick(item.slug)}
              >
                <img
                  src={item.imgSrc}
                  alt={item.title}
                  className="w-full h-auto object-cover"
                />

                <p
                  className="
                    mt-1 m-4 italic text-sm font-light
                    transition-transform duration-300 ease-out
                    group-hover:-translate-x-4
                  "
                >
                  {item.title}
                </p>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </Layout>
  );
}

export default Gallery;
